import { normalizeSize } from '@theme/size-quiz-core';

const Theme = window.Theme || {};
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function formatMoney(cents) {
  return Theme.formatMoney ? Theme.formatMoney(cents) : `$${(Number(cents) / 100).toFixed(2)}`;
}

function formatShortMoney(cents) {
  const value = Math.round(Number(cents) || 0);
  if (!Theme.formatMoney) return `$${Math.round(value / 100)}`;
  if (value % 100 !== 0) return Theme.formatMoney(value);
  const format = (Theme.moneyFormat || '${{amount}}')
    .replace(/\{\{\s*amount\s*\}\}/, '{{amount_no_decimals}}')
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, '{{amount_no_decimals_with_comma_separator}}')
    .replace(/\{\{\s*amount_with_space_separator\s*\}\}/, '{{amount_no_decimals_with_space_separator}}');
  return Theme.formatMoney(value, format);
}

function currencyRate() {
  const rate = Number(window.Shopify?.currency?.rate);
  return Number.isFinite(rate) && rate > 0 ? rate : 1;
}

function scrollBehavior() {
  return reducedMotion.matches ? 'auto' : 'smooth';
}

class MediaGallery extends HTMLElement {
  connectedCallback() {
    if (this._ready) {
      document.addEventListener('variant:change', this.onVariantChange);
      if (this.track) this.resizeObserver?.observe(this.track);
      return;
    }
    this._ready = true;
    this.track = this.querySelector('[data-track]');
    this.thumbList = this.querySelector('[data-thumbs]');
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.counter = this.querySelector('[data-counter]');
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this.lightbox = this.querySelector('[data-lightbox]');
    this.slides = this.track ? [...this.track.children] : [];
    this.index = 0;

    this.prev?.addEventListener('click', () => this.go(this.index - 1));
    this.next?.addEventListener('click', () => this.go(this.index + 1));
    this.track?.addEventListener('scroll', () => this.onScroll(), { passive: true });
    if (this.track && this.slides.length > 1) this.initMouseDrag();
    this.querySelector('.pmg__viewport')?.addEventListener('keydown', (event) => this.onKeydown(event));
    this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.go(Number(thumb.dataset.thumb))));
    // The thumbnail strip follows the main track only once a swipe settles, and never while it's being touched.
    if (this.thumbList) {
      const release = () => {
        clearTimeout(this.thumbsReleaseTimer);
        this.thumbsReleaseTimer = setTimeout(() => (this.thumbsHeld = false), 400);
      };
      this.thumbList.addEventListener('pointerdown', () => {
        clearTimeout(this.thumbsReleaseTimer);
        this.thumbsHeld = true;
      }, { passive: true });
      this.thumbList.addEventListener('pointerup', release, { passive: true });
      this.thumbList.addEventListener('pointercancel', release, { passive: true });
    }

    if (this.track && 'ResizeObserver' in window) {
      let width = this.track.clientWidth;
      this.resizeObserver = new ResizeObserver(() => {
        const next = this.track.clientWidth;
        if (!next || next === width) return;
        width = next;
        const slide = this.slides[this.index];
        if (slide) this.track.scrollTo({ left: this.slideOffset(slide), behavior: 'auto' });
      });
      this.resizeObserver.observe(this.track);
    }

    this.querySelectorAll('[data-zoom-index]').forEach((button) => {
      button.addEventListener('click', () => this.openLightbox?.(Number(button.dataset.zoomIndex)));
    });

    if (this.lightbox) this.initLightbox();

    this.onVariantChange = (event) => {
      if (event.detail?.sectionId !== this.dataset.sectionId) return;
      const mediaId = event.detail.variant?.featured_media?.id;
      if (!mediaId) return;
      const slideIndex = this.slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
      if (slideIndex > -1) this.go(slideIndex);
    };
    document.addEventListener('variant:change', this.onVariantChange);

    const start = Number(this.dataset.startIndex || 0);
    if (start > 0) {
      requestAnimationFrame(() => this.go(start, { smooth: false }));
    } else {
      this.syncUi();
    }
  }

  disconnectedCallback() {
    clearTimeout(this.thumbSyncTimer);
    clearTimeout(this.thumbsReleaseTimer);
    document.removeEventListener('variant:change', this.onVariantChange);
    this.resizeObserver?.disconnect();
  }

  slideOffset(slide) {
    return slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
  }

  // Touch swipes scroll the track natively; this gives mouse users (and the editor's mobile preview) the same swipe.
  initMouseDrag() {
    const track = this.track;
    let startX = 0;
    let startScroll = 0;
    let pointerId = null;
    let moved = false;

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (track.scrollWidth <= track.clientWidth + 2) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = track.scrollLeft;
      moved = false;
    });

    track.addEventListener('pointermove', (event) => {
      if (pointerId !== event.pointerId) return;
      const delta = event.clientX - startX;
      if (!moved && Math.abs(delta) < 6) return;
      if (!moved) {
        moved = true;
        track.setPointerCapture(pointerId);
        track.classList.add('is-dragging');
      }
      track.scrollLeft = startScroll - delta;
    });

    const end = (event) => {
      if (pointerId !== event.pointerId) return;
      pointerId = null;
      if (!moved) return;
      track.classList.remove('is-dragging');
      const delta = event.clientX - startX;
      const step = Math.abs(delta) > 40 ? (delta < 0 ? 1 : -1) : 0;
      const from = this.slides.findIndex((slide) => Math.abs(this.slideOffset(slide) - startScroll) < 4);
      this.go((from > -1 ? from : this.index) + step);
    };
    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);

    track.addEventListener(
      'click',
      (event) => {
        if (!moved) return;
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      },
      true
    );
    track.addEventListener('dragstart', (event) => {
      if (event.target.closest('img')) event.preventDefault();
    });
  }

  onKeydown(event) {
    if (!this.track || event.altKey || event.ctrlKey || event.metaKey || event.target.closest('video, iframe')) return;
    const map = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1 };
    if (event.target === this.track) Object.assign(map, { Home: 0, End: this.slides.length - 1 });
    if (!(event.key in map)) return;
    event.preventDefault();
    this.go(map[event.key]);
  }

  onScroll() {
    cancelAnimationFrame(this._frame);
    this._frame = requestAnimationFrame(() => {
      if (!this.track || !this.slides.length) return;
      const left = this.track.scrollLeft;
      let best = 0;
      let distance = Infinity;
      this.slides.forEach((slide, i) => {
        const delta = Math.abs(this.slideOffset(slide) - left);
        if (delta < distance) {
          distance = delta;
          best = i;
        }
      });
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (max > 0 && left >= max - 2) best = Math.max(best, this.lastReachableIndex());
      clearTimeout(this.thumbSyncTimer);
      this.thumbSyncTimer = setTimeout(() => this.syncThumbs(), 150);
      if (best === this.index) return;
      this.index = best;
      if (window.SSI && Date.now() - (this._goAt || 0) > 1000) {
        this._swiped ??= new Set([0]);
        if (!this._swiped.has(best)) {
          this._swiped.add(best);
          Theme.ss?.('feat', 'image_swipe', { idx: best, n: this.slides.length });
        }
      }
      this.syncUi({ thumbs: false });
    });
  }

  lastReachableIndex() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    let last = 0;
    this.slides.forEach((slide, i) => {
      if (this.slideOffset(slide) <= max + 2) last = i;
    });
    return last;
  }

  go(index, { smooth = true } = {}) {
    if (!this.track || !this.slides.length) return;
    const next = Math.max(0, Math.min(index, this.slides.length - 1));
    this.index = next;
    this._goAt = Date.now();
    this.track.scrollTo({ left: this.slideOffset(this.slides[next]), behavior: smooth ? scrollBehavior() : 'auto' });
    this.syncUi();
  }

  syncUi({ thumbs = true } = {}) {
    if (this.counter) this.counter.textContent = String(this.index + 1);
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.index));
    this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
    this.slides.forEach((slide, i) => slide.toggleAttribute('data-active', i === this.index));
    if (this.prev) this.prev.disabled = this.index <= 0;
    if (this.next) this.next.disabled = this.index >= this.slides.length - 1;

    if (thumbs) this.syncThumbs();
  }

  syncThumbs() {
    const thumb = this.thumbs[this.index];
    if (!thumb || !this.thumbList || this.thumbsHeld || this.thumbList.scrollWidth <= this.thumbList.clientWidth) return;
    const item = thumb.parentElement.getBoundingClientRect();
    const list = this.thumbList.getBoundingClientRect();
    const target = this.thumbList.scrollLeft + item.left - list.left - (list.width - item.width) / 2;
    if (Math.abs(target - this.thumbList.scrollLeft) < 2) return;
    this.thumbList.scrollTo({ left: Math.max(0, target), behavior: scrollBehavior() });
  }

  initLightbox() {
    const dialog = this.lightbox;
    const track = dialog.querySelector('[data-lightbox-track]');
    const slides = track ? [...track.children] : [];
    const counter = dialog.querySelector('[data-lightbox-counter]');
    const close = dialog.querySelector('[data-lightbox-close]');
    const prev = dialog.querySelector('[data-lightbox-prev]');
    const next = dialog.querySelector('[data-lightbox-next]');
    const zooms = [...dialog.querySelectorAll('[data-lightbox-zoom]')];
    let lbIndex = 0;

    const resetZoom = () => {
      zooms.forEach((zoom) => {
        zoom.classList.remove('is-zoomed');
        zoom.setAttribute('aria-pressed', 'false');
      });
    };

    const sync = () => {
      if (counter) counter.textContent = String(lbIndex + 1);
      if (prev) prev.disabled = lbIndex <= 0;
      if (next) next.disabled = lbIndex >= slides.length - 1;
    };

    const goLb = (index, smooth = true) => {
      if (!track || !slides.length) return;
      lbIndex = Math.max(0, Math.min(index, slides.length - 1));
      resetZoom();
      track.scrollTo({ left: lbIndex * track.clientWidth, behavior: smooth ? scrollBehavior() : 'auto' });
      sync();
    };

    track?.addEventListener(
      'scroll',
      () => {
        if (!track.clientWidth) return;
        const current = Math.round(track.scrollLeft / track.clientWidth);
        if (current !== lbIndex) {
          lbIndex = current;
          resetZoom();
          sync();
        }
      },
      { passive: true }
    );

    prev?.addEventListener('click', () => goLb(lbIndex - 1));
    next?.addEventListener('click', () => goLb(lbIndex + 1));
    close?.addEventListener('click', () => dialog.close());

    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goLb(lbIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goLb(lbIndex + 1);
      }
    });

    const setOrigin = (zoom, event) => {
      const rect = zoom.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      zoom.style.setProperty('--zoom-x', `${Math.max(0, Math.min(100, x))}%`);
      zoom.style.setProperty('--zoom-y', `${Math.max(0, Math.min(100, y))}%`);
    };

    zooms.forEach((zoom) => {
      zoom.addEventListener('click', (event) => {
        if (event.detail > 0) setOrigin(zoom, event);
        const zoomed = !zoom.classList.contains('is-zoomed');
        zoom.classList.toggle('is-zoomed', zoomed);
        zoom.setAttribute('aria-pressed', String(zoomed));
      });
      zoom.addEventListener('pointermove', (event) => {
        if (event.pointerType === 'mouse' && zoom.classList.contains('is-zoomed')) setOrigin(zoom, event);
      });
    });

    let opener = null;
    this.openLightbox = (imageIndex) => {
      lbIndex = imageIndex;
      opener = this.querySelector(`[data-zoom-index="${imageIndex}"]`) || document.activeElement;
      dialog.showModal();
      Theme.lockScroll?.(true);
      requestAnimationFrame(() => goLb(lbIndex, false));
    };

    dialog.addEventListener('close', () => {
      resetZoom();
      Theme.lockScroll?.(false);
      opener?.focus?.({ preventScroll: true });
      opener = null;
    });
  }
}

class GiftTiers extends HTMLElement {
  // product-info can call setProductPrice before this element's connectedCallback (section reload).
  init() {
    if (this.tiers) return;
    this.tiers = [...this.querySelectorAll('[data-threshold]')];
    this.progress = this.querySelector('[data-gift-progress]');
    this.productPrice ??= Number(this.dataset.productPrice) || 0;
    this.update = () => this.refresh();
    this.onVariant = (event) => {
      const variant = event.detail?.variant;
      if (!variant || event.detail?.sectionId === 'quick-view-product') return;
      this.setProductPrice(variant.available ? variant.price : 0);
    };
  }

  connectedCallback() {
    this.init();
    document.addEventListener('cart:updated', this.update);
    if (!this.closest('product-info')) document.addEventListener('variant:change', this.onVariant);
    if (this._loaded) return;
    this._loaded = true;
    if (Theme.cart?.state) {
      this.refresh();
    } else if (Theme.cart?.get) {
      Theme.cart.get().then(this.update, this.update);
    } else {
      this.refresh();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this.update);
    document.removeEventListener('variant:change', this.onVariant);
  }

  setProductPrice(cents) {
    this.init();
    this.productPrice = Number(cents) || 0;
    this.refresh();
  }

  eligibleSubtotal(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const isGift = (item) => {
      const value = item?.properties?._free_gift;
      return value !== undefined && value !== null && value !== '';
    };
    const lineTotal = (item) => Number(item.final_line_price ?? item.line_price ?? 0);
    const paid = items.filter((item) => !isGift(item));
    if (!paid.length) return 0;
    const giftTotal = items.filter(isGift).reduce((sum, item) => sum + lineTotal(item), 0);
    const base = Number.isFinite(cart?.items_subtotal_price)
      ? cart.items_subtotal_price - giftTotal
      : paid.reduce((sum, item) => sum + lineTotal(item), 0);
    return Math.max(0, Math.round(base));
  }

  refresh() {
    const rate = currencyRate();
    const subtotal = this.eligibleSubtotal(Theme.cart?.state);
    let nextTier = null;
    let unlockableByAdding = null;

    this.tiers.forEach((tier) => {
      const threshold = Math.round(Number(tier.dataset.threshold || 0) * rate);
      const reached = subtotal > 0 && subtotal >= threshold;
      tier.classList.toggle('is-reached', reached);
      tier.querySelectorAll('[data-tier-spend]').forEach((spend) => {
        const template = spend.dataset.template;
        if (template) spend.textContent = template.replace('[amount]', formatShortMoney(threshold));
      });
      const status = tier.querySelector('[data-tier-status]');
      if (status) status.textContent = reached ? status.dataset.reached || '' : '';
      if (!reached) {
        if (!nextTier || threshold < nextTier.threshold) nextTier = { threshold, gift: tier.dataset.gift || '' };
        if (this.productPrice > 0 && subtotal + this.productPrice >= threshold) {
          if (!unlockableByAdding || threshold > unlockableByAdding.threshold) {
            unlockableByAdding = { threshold, gift: tier.dataset.gift || '' };
          }
        }
      }
    });

    if (!this.progress) return;
    let message = '';
    if (unlockableByAdding && this.dataset.addTemplate) {
      message = this.dataset.addTemplate.replace('[gift]', unlockableByAdding.gift);
    } else if (nextTier && this.dataset.progressTemplate) {
      message = this.dataset.progressTemplate
        .replace('[amount]', formatShortMoney(nextTier.threshold - subtotal))
        .replace('[gift]', nextTier.gift);
    } else if (!nextTier && this.tiers.length) {
      message = this.dataset.complete || '';
    }
    if (this.progress.textContent !== message) this.progress.textContent = message;
    this.progress.hidden = !message;
  }
}

class ProductInfo extends HTMLElement {
  connectedCallback() {
    // Moving the section in the theme editor reconnects this element without rebuilding it.
    if (this._ready) {
      if (!this.data) return;
      document.addEventListener('sizequiz:select', this.onQuizSelect);
      this.initSticky();
      this.initStickyColumns();
      return;
    }
    this._ready = true;
    this.sectionId = this.dataset.sectionId;
    const jsonEl = this.querySelector('[data-product-variants]');
    if (!jsonEl) return;
    try {
      this.data = JSON.parse(jsonEl.textContent);
    } catch {
      return;
    }
    this.variants = this.data.variants || [];
    this.form = this.querySelector('form[data-ajax-cart]');
    this.idInput = this.form?.querySelector('[name="id"]');
    this.picker = this.querySelector('[data-variant-picker]');
    this.fieldsets = this.picker ? [...this.picker.querySelectorAll('fieldset[data-option-position]')] : [];
    this.priceRoot = this.querySelector('[data-product-price]');
    this.errorEl = this.querySelector('[data-product-error]');
    this.giftTiers = [...this.querySelectorAll('gift-tiers')];
    this.sticky = document.querySelector(`[data-sticky-atc][data-section-id="${this.sectionId}"]`);
    this.atcButtons = [
      ...this.querySelectorAll('[data-product-atc]'),
      ...(this.sticky ? this.sticky.querySelectorAll('[data-product-atc]') : []),
    ];

    this.variantMap = new Map();
    this.variants.forEach((variant) => this.variantMap.set((variant.options || []).join('|||'), variant));
    // Liquid only lists the first 250 variants; other combinations are fetched when picked.
    this.partial = Number(this.data.variantsCount) > this.variants.length;
    this.missingVariants = new Set();
    if (this.data.current) this.rememberVariant(this.data.current);

    this.ensureInitialSelection();
    this.preselectBagSize();

    this.picker?.addEventListener('change', (event) => {
      if (!event.target.matches('input[type="radio"]')) return;
      this.onOptionChange();
      this.trackVariant(event.target, 'picker');
    });

    this.form?.addEventListener('submit', (event) => this.onSubmit(event));

    this.onQuizSelect = (event) => {
      if (event.detail?.sectionId !== this.sectionId) return;
      this.selectSizeFromQuiz(event.detail.size);
    };
    document.addEventListener('sizequiz:select', this.onQuizSelect);

    this.onOptionChange({ initial: true });
    this.initSticky();
    this.initRestock();
    this.initStickyColumns();
  }

  disconnectedCallback() {
    document.removeEventListener('sizequiz:select', this.onQuizSelect);
    this.stickyObserver?.disconnect();
    this.columnObserver?.disconnect();
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    if (this.onStickyResize) window.removeEventListener('resize', this.onStickyResize);
    document.body.classList.remove('has-sticky-atc', 'atc-in-view', 'size-sheet-open');
    document.documentElement.style.removeProperty('--sticky-atc-height');
    this.sizeSheet?.remove();
    this.sizeSheet = null;
  }

  ensureInitialSelection() {
    const initialId = Number(this.idInput?.value);
    const initial = this.variants.find((variant) => variant.id === initialId) || this.variants[0];
    if (!initial) return;
    this.fieldsets.forEach((fieldset) => {
      if (fieldset.querySelector('input[type="radio"]:checked') || fieldset.hasAttribute('data-size-required')) return;
      const value = initial.options?.[Number(fieldset.dataset.optionPosition) - 1];
      const input = [...fieldset.querySelectorAll('input[type="radio"]')].find((radio) => radio.value === value);
      if (input) input.checked = true;
    });
  }

  // A size the shopper already added to the bag is picked for them when this product has it in stock.
  preselectBagSize() {
    const fieldset = this.picker?.querySelector('[data-size-option][data-size-required]');
    if (!fieldset || fieldset.querySelector('input[type="radio"]:checked')) return;
    const index = this.fieldsets.indexOf(fieldset);
    const radios = [...fieldset.querySelectorAll('input[type="radio"]')];
    for (const size of window.Theme?.bagSizes?.() || []) {
      const target = normalizeSize(size);
      const input = radios.find((radio) => normalizeSize(radio.value) === target);
      if (!input) continue;
      const options = this.getSelectedOptions();
      options[index] = input.value;
      if (!this.findVariant(options)?.available) continue;
      input.checked = true;
      return;
    }
  }

  sizeMissing() {
    const fieldset = this.picker?.querySelector('[data-size-option][data-size-required]');
    return Boolean(fieldset && !fieldset.querySelector('input[type="radio"]:checked'));
  }

  getSelectedOptions() {
    return this.fieldsets.map((fieldset) => fieldset.querySelector('input[type="radio"]:checked')?.value ?? '');
  }

  findVariant(options) {
    if (!this.fieldsets.length) {
      const id = Number(this.idInput?.value);
      return this.variants.find((variant) => variant.id === id) || this.data.current || this.variants[0] || null;
    }
    return this.variantMap.get(options.join('|||')) || null;
  }

  rememberVariant(variant) {
    const key = (variant?.options || []).join('|||');
    if (!variant?.id || this.variantMap.has(key)) return;
    this.variants.push(variant);
    this.variantMap.set(key, variant);
  }

  async loadVariant(options) {
    const key = options.join('|||');
    const ids = this.fieldsets.map((fieldset) => fieldset.querySelector('input[type="radio"]:checked')?.dataset.optionValueId);
    if (this.loadingVariant === key || ids.some((id) => !id)) return;
    this.loadingVariant = key;
    this.atcButtons.forEach((button) => button.setAttribute('aria-busy', 'true'));
    try {
      const url = new URL(this.data.url || window.location.pathname, window.location.origin);
      url.searchParams.set('option_values', ids.join(','));
      url.searchParams.set('section_id', this.sectionId);
      const response = await fetch(url);
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const found = JSON.parse(doc.querySelector('[data-product-variants]')?.textContent || '{}').current;
      if (found && (found.options || []).join('|||') === key) this.rememberVariant(found);
      else this.missingVariants.add(key);
    } catch {
      this.missingVariants.add(key);
    } finally {
      this.loadingVariant = null;
      this.atcButtons.forEach((button) => button.removeAttribute('aria-busy'));
    }
    if (this.getSelectedOptions().join('|||') === key) this.onOptionChange();
  }

  onOptionChange({ initial = false } = {}) {
    const options = this.getSelectedOptions();
    const variant = this.findVariant(options);
    this.currentVariant = variant;
    if (!variant && this.partial && !options.includes('') && !this.missingVariants.has(options.join('|||'))) this.loadVariant(options);

    this.fieldsets.forEach((fieldset) => {
      const selected = fieldset.querySelector('[data-selected-value]');
      if (selected) selected.textContent = options[Number(fieldset.dataset.optionPosition) - 1] || '';
    });

    this.updateOptionAvailability(options);
    if (this.idInput) this.idInput.value = variant ? variant.id : '';
    this.showError('');
    const matches = (entry) => options.every((value, index) => !value || entry.options?.[index] === value);
    const priceVariant = variant || (this.sizeMissing() ? this.variants.find((entry) => entry.available && matches(entry)) || this.variants.find((entry) => entry.available) : null);
    this.updatePrice(priceVariant);
    this.updateAvailability(variant);
    this.updateSticky(variant);
    this.giftTiers.forEach((tiers) => tiers.setProductPrice?.(priceVariant?.available ? priceVariant.price : 0));
    if (variant && !initial) this.updateUrl(variant);

    if (!initial && variant) {
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant, sectionId: this.sectionId } }));
    }
  }

  updateOptionAvailability(options) {
    this.fieldsets.forEach((fieldset) => {
      const position = Number(fieldset.dataset.optionPosition) - 1;
      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        const candidate = [...options];
        candidate[position] = input.value;
        const available = candidate.includes('')
          ? this.variants.some((variant) => variant.available && candidate.every((value, index) => value === '' || variant.options?.[index] === value))
          : this.variantMap.has(candidate.join('|||'))
            ? Boolean(this.variantMap.get(candidate.join('|||')).available)
            : this.partial && !this.missingVariants.has(candidate.join('|||'));
        const label = fieldset.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        if (!label) return;
        label.toggleAttribute('data-unavailable', !available);
        const hint = label.querySelector('[data-unavailable-label]');
        if (hint) hint.hidden = available;
      });
    });
  }

  updateUrl(variant) {
    if (window.Shopify?.designMode) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({ ...(window.history.state || {}), variant: variant.id }, '', url);
  }

  priceText(cents) {
    const text = formatMoney(cents);
    const code = this.priceRoot?.dataset.currencyCode;
    return code && !text.includes(code) ? `${text} ${code}` : text;
  }

  updatePrice(variant) {
    const stickyPrice = this.sticky?.querySelector('[data-sticky-price]');
    if (!variant) return;
    const price = Number(variant.price);
    const compare = Number(variant.compare_at_price || 0);
    const onSale = compare > price;
    if (stickyPrice) stickyPrice.textContent = formatMoney(price);
    if (!this.priceRoot) return;
    const current = this.priceRoot.querySelector('[data-price-current]');
    const compareEl = this.priceRoot.querySelector('[data-price-compare]');
    const compareLabel = this.priceRoot.querySelector('[data-price-label-compare]');
    const currentLabel = this.priceRoot.querySelector('[data-price-label-current]');
    const saveBadge = this.priceRoot.querySelector('[data-price-save]');
    if (current) current.textContent = this.priceText(price);
    if (currentLabel && current) {
      currentLabel.textContent = onSale ? current.dataset.labelSale || '' : current.dataset.labelRegular || '';
    }
    if (compareEl) {
      compareEl.textContent = onSale ? this.priceText(compare) : '';
      compareEl.hidden = !onSale;
    }
    if (compareLabel) compareLabel.hidden = !onSale;
    if (saveBadge) {
      const save = onSale ? compare - price : 0;
      const label = saveBadge.dataset.label || '';
      const amount = formatMoney(save);
      saveBadge.textContent = save > 0 ? (label.includes('[amount]') ? label.replace('[amount]', amount) : amount) : '';
      saveBadge.hidden = save <= 0;
    }
    this.priceRoot.classList.toggle('is-on-sale', onSale);
  }

  updateAvailability(variant) {
    const available = Boolean(variant?.available);
    const needsSize = this.sizeMissing();
    this.atcButtons.forEach((button) => {
      let label = button.dataset.addLabel || Theme.strings?.addToCart || 'Add to bag';
      if (needsSize) {
        label = button.dataset.selectSizeLabel || 'Select size';
        button.disabled = false;
        button.toggleAttribute('data-needs-size', true);
        if (button.textContent.trim() !== label) button.textContent = label;
        return;
      }
      button.removeAttribute('data-needs-size');
      if (!variant) label = button.dataset.unavailableLabel || Theme.strings?.soldOut || 'Unavailable';
      else if (!available) label = button.dataset.soldOutLabel || Theme.strings?.soldOut || 'Sold out';
      button.disabled = !available;
      if (button.textContent.trim() !== label) button.textContent = label;
    });

    const urgency = this.querySelector('[data-inventory-urgency]');
    if (urgency) {
      const qty = Number(variant?.inventory_quantity || 0);
      const managed = variant?.inventory_management === 'shopify';
      const threshold = Number(urgency.dataset.threshold || 5);
      const show = Boolean(variant) && managed && available && qty > 0 && qty <= threshold;
      urgency.hidden = !show;
      const text = urgency.querySelector('[data-inventory-text]') || urgency;
      if (show && urgency.dataset.template) text.textContent = urgency.dataset.template.replace('[count]', String(qty));
    }
  }

  updateSticky(variant) {
    const label = this.sticky?.querySelector('[data-sticky-size]');
    if (!label) return;
    label.textContent = variant && this.fieldsets.length ? variant.title || '' : '';
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.hidden = !message;
  }

  setBusy(busy) {
    this.atcButtons.forEach((button) => {
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    });
  }

  trackVariant(input, via) {
    if (!window.SSI) return;
    Theme.ss?.('feat', 'variant_select', {
      option: (input.name.match(/\[(.+)\]/) || [])[1] || input.name,
      value: input.value,
      variant: this.currentVariant?.id ?? null,
      via,
    });
  }

  onSubmit(event) {
    const fromSticky = Boolean(event.submitter && this.sticky?.contains(event.submitter));
    if (this.sizeMissing()) {
      event.preventDefault();
      event.stopPropagation();
      this.promptSize(fromSticky);
      return;
    }
    if (!Theme.cart?.add || Theme.settings?.cartType === 'page') return;
    event.preventDefault();
    event.stopPropagation();
    const variant = this.currentVariant;
    if (!variant?.available || this._submitting) return;

    const data = new FormData(this.form);
    const properties = {};
    for (const [key, value] of data.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value !== '') properties[match[1]] = value;
    }
    const quantity = Math.max(1, Math.floor(Number(data.get('quantity')) || 1));
    const item = { id: variant.id, quantity, properties };
    const sellingPlan = data.get('selling_plan');
    if (sellingPlan) item.selling_plan = Number(sellingPlan);

    this._submitting = true;
    this.showError('');
    this.setBusy(true);
    Theme.cart
      .add([item], { source: 'product-form', errorToast: false, ...(fromSticky ? { track: 'sticky' } : {}) })
      .catch((error) => this.showError(error?.message || Theme.strings?.error || 'Error'))
      .finally(() => {
        this._submitting = false;
        this.setBusy(false);
      });
  }

  promptSize(fromSticky = false) {
    const fieldset = this.picker?.querySelector('[data-size-option]');
    if (!fieldset) return;
    if (fromSticky || window.matchMedia('(max-width: 749px)').matches) {
      this.openSizeSheet(fieldset);
      return;
    }
    fieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
    fieldset.classList.remove('is-prompted');
    void fieldset.offsetWidth;
    fieldset.classList.add('is-prompted');
    this.showError(this.dataset.sizeRequired || '');
  }

  openSizeSheet(fieldset) {
    let sheet = this.sizeSheet;
    if (!sheet) {
      sheet = document.createElement('dialog');
      sheet.className = 'size-sheet';
      sheet.setAttribute('aria-labelledby', `SizeSheet-${this.sectionId}`);
      sheet.innerHTML = `<div class="size-sheet__panel"><div class="size-sheet__head"><p class="size-sheet__title" id="SizeSheet-${this.sectionId}"></p><button type="button" class="size-sheet__close" data-size-sheet-close aria-label="${Theme.strings?.close || 'Close'}">&times;</button></div><div class="size-sheet__grid" role="group" data-size-sheet-grid></div></div>`;
      sheet.querySelector('.size-sheet__title').textContent = this.dataset.sizeSheetTitle || fieldset.querySelector('.variant-picker__name')?.textContent || '';
      sheet.addEventListener('click', (event) => {
        if (event.target === sheet || event.target.closest('[data-size-sheet-close]')) sheet.close();
        const choice = event.target.closest('[data-size-value]');
        if (!choice || choice.disabled) return;
        const input = [...fieldset.querySelectorAll('input[type="radio"]')].find((radio) => radio.value === choice.dataset.sizeValue);
        if (!input) return;
        input.checked = true;
        this.onOptionChange();
        this.trackVariant(input, 'sheet');
        sheet.close();
        this.sticky?.querySelector('[data-product-atc]')?.focus({ preventScroll: true });
      });
      sheet.addEventListener('close', () => document.body.classList.remove('size-sheet-open'));
      document.body.append(sheet);
      this.sizeSheet = sheet;
    }
    const grid = sheet.querySelector('[data-size-sheet-grid]');
    grid.replaceChildren(
      ...[...fieldset.querySelectorAll('input[type="radio"]')].map((radio) => {
        const label = fieldset.querySelector(`label[for="${CSS.escape(radio.id)}"]`);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'size-sheet__option';
        button.dataset.sizeValue = radio.value;
        button.textContent = radio.value;
        if (label?.hasAttribute('data-unavailable')) {
          button.disabled = true;
          button.classList.add('is-unavailable');
        }
        return button;
      })
    );
    document.body.classList.add('size-sheet-open');
    if (sheet.open) return;
    sheet.showModal();
    sheet.scrollTop = 0;
  }

  selectSizeFromQuiz(size) {
    const fieldset = this.picker?.querySelector('[data-size-option]');
    if (!fieldset) return;
    const target = normalizeSize(size);
    const input = [...fieldset.querySelectorAll('input[type="radio"]')].find((radio) => normalizeSize(radio.value) === target);
    if (!input) return;
    input.checked = true;
    const label = fieldset.querySelector(`label[for="${CSS.escape(input.id)}"]`);
    label?.classList.remove('is-highlighted');
    void label?.offsetWidth;
    label?.classList.add('is-highlighted');
    setTimeout(() => label?.classList.remove('is-highlighted'), 1200);
    this.onOptionChange();
    this.trackVariant(input, 'size_finder');
  }

  // Sold-out products: Get reminded opens a sheet that signs the shopper up for a restock email.
  initRestock() {
    const dialog = this.querySelector('[data-restock-dialog]');
    if (!dialog) return;
    const form = dialog.querySelector('[data-restock-form]');
    const open = () => {
      if (dialog.open) return;
      dialog.showModal();
      document.body.classList.add('size-sheet-open');
    };
    const onClick = (event) => {
      if (!event.target.closest('[data-restock-open]')) return;
      event.preventDefault();
      open();
    };
    this.addEventListener('click', onClick);
    this.sticky?.addEventListener('click', onClick);
    let pressedBackdrop = false;
    dialog.addEventListener('pointerdown', (event) => {
      pressedBackdrop = event.target === dialog;
    });
    dialog.addEventListener('click', (event) => {
      if ((event.target === dialog && pressedBackdrop) || event.target.closest('[data-restock-close]')) dialog.close();
      pressedBackdrop = false;
    });
    dialog.addEventListener('close', () => document.body.classList.remove('size-sheet-open'));
    form?.addEventListener('submit', (event) => this.submitRestock(event, form));
    if (form && window.location.hash === `#${form.id}`) open();
  }

  // Posts the customer form in the background; anything but a clean success (a captcha challenge,
  // a validation error) falls back to a normal submit so Shopify can handle it.
  async submitRestock(event, form) {
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    if (button?.getAttribute('aria-busy') === 'true') return;
    button?.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(form.action, { method: 'POST', body: new FormData(form) });
      if (!response.ok || !response.url.includes('customer_posted=true')) throw new Error('fallback');
      form.querySelector('[data-restock-fields]').hidden = true;
      form.querySelector('[data-restock-success]').hidden = false;
      Theme.ss?.('feat', 'restock_signup', { variant: this.currentVariant?.id ?? null });
    } catch {
      HTMLFormElement.prototype.submit.call(form);
    } finally {
      button?.removeAttribute('aria-busy');
    }
  }

  initSticky() {
    const mainAtc = this.querySelector('[data-main-atc]');
    const bar = this.sticky;
    if (!mainAtc || !bar || !('IntersectionObserver' in window) || Theme.abOff?.('sticky_atc')) return;
    const root = document.documentElement;
    let visible = false;

    const setHeight = () => {
      root.style.setProperty('--sticky-atc-height', visible ? `${bar.offsetHeight}px` : '0px');
    };

    const toggle = (show) => {
      if (show === visible) return;
      visible = show;
      bar.classList.toggle('is-visible', show);
      bar.toggleAttribute('inert', !show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      document.body.classList.toggle('has-sticky-atc', show && bar.offsetHeight > 0);
      setHeight();
    };

    this.stickyObserver = new IntersectionObserver(([entry]) => {
      document.body.classList.toggle('atc-in-view', entry.isIntersecting);
      toggle(!entry.isIntersecting);
      requestAnimationFrame(() => {
        setHeight();
        document.body.classList.toggle('has-sticky-atc', visible && bar.offsetHeight > 0);
      });
    });
    this.stickyObserver.observe(mainAtc);
    this.onStickyResize = Theme.debounce ? Theme.debounce(setHeight, 150) : setHeight;
    window.addEventListener('resize', this.onStickyResize);
  }

  initStickyColumns() {
    const columns = [...this.querySelectorAll(':scope > [data-sticky-column]')];
    if (columns.length < 2 || !('ResizeObserver' in window)) return;
    const gap = 24;
    const update = () => {
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 72;
      columns.forEach((column) => {
        const top = Math.min(header + gap, window.innerHeight - column.offsetHeight - gap);
        column.style.setProperty('--sticky-top', `${Math.round(top)}px`);
      });
    };
    this.columnObserver = new ResizeObserver(update);
    columns.forEach((column) => this.columnObserver.observe(column));
    this.onResize = update;
    window.addEventListener('resize', this.onResize, { passive: true });
    update();
  }
}

class ProductRecommendations extends HTMLElement {
  disconnectedCallback() {
    if (!this.observer) return;
    this.observer.disconnect();
    if (!this.classList.contains('is-loaded')) this._started = false;
  }

  connectedCallback() {
    if (this._started) return;
    this._started = true;
    if (!this.dataset.url || !this.querySelector('[data-recommendations]')) return;

    const start = () => this.load(this.dataset.url, this.dataset.fallbackUrl);
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          start();
        },
        { rootMargin: '400px 0px' }
      );
      this.observer.observe(this);
    } else {
      start();
    }
  }

  async load(url, fallbackUrl) {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = doc.querySelector('[data-recommendations]');
      if (!content || !content.querySelector('[data-recommendation-item]')) {
        if (fallbackUrl && fallbackUrl !== url) return this.load(fallbackUrl, null);
        this.useFallback();
        return;
      }
      this.show(content.innerHTML);
    } catch {
      this.useFallback();
    }
  }

  useFallback() {
    const template = this.querySelector('template[data-recommendations-fallback]');
    if (template?.content.querySelector('[data-recommendation-item]')) {
      this.show(template.innerHTML);
      return;
    }
    this.closest('[data-recommendations-section]')?.setAttribute('hidden', '');
  }

  show(html) {
    const target = this.querySelector('[data-recommendations]');
    target.innerHTML = html;
    this.classList.add('is-loaded');
    this.closest('[data-recommendations-section]')?.classList.add('is-loaded');
    Theme.initReveal?.();
    if (target.querySelector('slider-component') && !customElements.get('slider-component') && this.dataset.sliderSrc) {
      import(this.dataset.sliderSrc).catch(() => {});
    }
  }
}

function initFitGuideDialogs(root = document) {
  root.querySelectorAll('[data-fit-guide-open]').forEach((button) => {
    if (button.dataset.fitGuideBound) return;
    button.dataset.fitGuideBound = 'true';
    const dialog = button.parentElement?.querySelector('[data-fit-guide-dialog]');
    if (!dialog) return;
    button.addEventListener('click', () => dialog.showModal());
    dialog.querySelector('[data-fit-guide-close]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initFitGuideDialogs());
} else {
  initFitGuideDialogs();
}

if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => initFitGuideDialogs(event.target));
}

if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);
if (!customElements.get('gift-tiers')) customElements.define('gift-tiers', GiftTiers);
if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);
if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);
